import { useParentPosition } from "@/hooks/useParentPosition";
import { parseDateString, parseToUTCDate } from "@/lib/utils/parser";
import { IconCalendar, IconX } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { DatePicker } from "../../DatePicker/DatePicker";
import {
  boundToDate,
  DATE_DROPDOWN_MAX_DAYS,
  DateRangeDropdown,
  daySpan,
} from "../../DatePicker/DateRangeDropdown";

// A DATE (date-only) value is a PURE CALENDAR DAY with no time-of-day, so it
// carries no timezone meaning — it is stored as UTC-midnight of the picked day.
// Frame it in UTC on all three sides (display, calendar picker, range dropdown)
// so pick → store → display is tz-INVARIANT: picking July 1 always stores
// UTC-midnight July 1 and always renders "01.07.2026", regardless of the event
// or browser tz. Framing it in the event tz double-shifted the day: a POSITIVE
// offset (CEST) floored the picker's wall-instant back to the previous UTC day
// on store (07-01 00:00 CEST = 06-30 22:00Z → stored 06-30), and a NEGATIVE
// offset (Americas) rolled the correct UTC-midnight forward a day on display.
const DATE_FRAME_TZ = "UTC";

type TableCellContentDateProps = {
  onFocusAction?: () => void;
  editable: boolean;
  isFilter?: boolean;
  border?: string;
  value: string;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  iconSize?: number;
  errorVisibility?: boolean;
  viewOnly?: boolean;
  /** IANA timezone for input + display. See TCCDateTime for fallback rules. */
  timeZone?: string;
  /**
   * Optional inclusive day bounds (field `lowerBound`/`upperBound`, or a
   * computed range like a slotting offering's window). Accept ISO-Z strings,
   * `"YYYY-MM-DD"`, or Date. When BOTH are set and the span is small
   * (≤ DATE_DROPDOWN_MAX_DAYS) and this is not a filter cell, the input renders
   * as a compact dropdown of the allowed days instead of a calendar; otherwise
   * the calendar simply disables out-of-range days.
   */
  minDate?: string | Date;
  maxDate?: string | Date;
};

export const TableCellContentDate: React.FC<TableCellContentDateProps> = ({
  editable,
  isFilter,
  border = "border outline-tertiary",
  value,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  error,
  height = "h-9",
  surface = "surface-primary",
  fontSize = "body-2-regular",
  padding = "px-2 py-2",
  rounded = "rounded-lg",
  iconSize = 16,
  errorVisibility = false,
  viewOnly,
  timeZone,
  minDate,
  maxDate,
}) => {
  // `timeZone` is accepted for API symmetry with TCCDateTime but intentionally
  // NOT used to frame the value — a date-only field is tz-invariant (see
  // DATE_FRAME_TZ above). Voided so a stray event-tz prop can't reintroduce the
  // day-shift.
  void timeZone;
  const tz = DATE_FRAME_TZ;

  // Constrained-range dropdown: both bounds set, small span, not a filter cell.
  const span = daySpan(minDate, maxDate);
  const useDropdown =
    !isFilter &&
    minDate != null &&
    maxDate != null &&
    span != null &&
    span <= DATE_DROPDOWN_MAX_DAYS;

  // Bounds for the calendar fallback (DatePicker wants Date | undefined).
  // Defensive: only Date/string are meaningful — anything else (e.g. a stray
  // numeric bound) becomes undefined rather than throwing on .toISOString().
  const minDateObj = boundToDate(minDate);
  const maxDateObj = boundToDate(maxDate);

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const { dropdownPosition, setDropdownPosition } = useParentPosition();
  const divRef = useRef<HTMLDivElement>(null);

  // Parse the initial value to a UTC Date object
  const [date, setDate] = useState<Date | undefined>(parseToUTCDate(value));

  // Update date when value changes
  useEffect(() => {
    setDate(parseToUTCDate(value));
  }, [value]);

  // Format date for display in the active tz
  const [formattedDate, setFormattedDate] = useState(
    parseDateString(date, "de-DE", tz),
  );

  // Update formatted date when date or tz changes
  useEffect(() => {
    setFormattedDate(parseDateString(date, "de-DE", tz));
  }, [date, tz]);

  // =========================================================
  // ROBUST AUTO-TRACKING IMPLEMENTATION
  // =========================================================
  useEffect(() => {
    if (datePickerOpen && divRef.current) {
      const updatePosition = () => {
        if (divRef.current) setDropdownPosition(divRef);
      };

      // 1. Immediate Failsafe:
      // Force calculation immediately when state becomes true to prevent (0,0) flicker.
      updatePosition();

      // 2. Live Tracking (Scroll & Resize):
      // capture: true is CRUCIAL for detecting scrolls in nested containers/tables.
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [datePickerOpen, setDropdownPosition]);

  // =========================================================
  // CENTRALIZED OPEN LOGIC
  // =========================================================
  const handleMouseClick = () => {
    // Prevent opening when viewOnly is true
    if (viewOnly) return;

    // 1. Calculate position FIRST
    if (divRef.current) {
      setDropdownPosition(divRef);
    }

    // 2. Then show component SECOND
    setDatePickerOpen(true);

    if (onFocusAction) onFocusAction();
  };

  const handleClearDate = (e: React.MouseEvent) => {
    // Prevent clearing when viewOnly is true
    if (viewOnly) return;

    e.stopPropagation(); // Prevent opening the date picker
    setDate(undefined); // Clear the date
    setFormattedDate(""); // Clear the displayed date
    onChangeValue(""); // Clear the value in the parent component
  };

  return (
    <>
      {isSelected && isEditing && (editable || isFilter) && useDropdown ? (
        <div className="flex-1 w-full min-w-0">
          <DateRangeDropdown
            value={value}
            minDate={minDate as string | Date}
            maxDate={maxDate as string | Date}
            onChangeValue={onChangeValue}
            timeZone={tz}
            disabled={viewOnly}
            error={error}
            border={border}
            height={height}
            fontSize={fontSize}
            padding={padding}
            rounded={rounded}
            surface={surface}
          />
        </div>
      ) : isSelected && isEditing && (editable || isFilter) ? (
        // `flex-1` (not bare `w-full`) so that inside a flex row — e.g. the
        // filter row, where the field-name InputColumn is also flex-1 — the
        // date value SHARES the width instead of grabbing basis:100% and
        // collapsing the sibling field-name input to zero. In a block/table
        // context flex-1 is inert and `w-full` still fills the cell.
        <div ref={divRef} className="flex-1 w-full min-w-0">
          <button
            onClick={handleMouseClick}
            disabled={viewOnly}
            className={`truncate overflow-hidden ${fontSize} ${height} flex ${surface} w-full ${rounded} ${padding} flex-row font-secondary items-center justify-between ${border} ${
              viewOnly ? "opacity-50 cursor-not-allowed" : ""
            } outline-quaternary`}
          >
            <div className="flex flex-row items-center">
              <IconCalendar size={iconSize} className="mr-2" />
              {/* suppressHydrationWarning: see TCCDateTime.tsx — locale-dependent. */}
              <p suppressHydrationWarning>{formattedDate}</p>
            </div>
            {value && !viewOnly && (
              <div
                data-testid="tcc-date-clear"
                onClick={handleClearDate}
                className="flex items-center hover:surface-secondary pl-1"
              >
                <IconX size={iconSize} className="font-secondary" />
              </div>
            )}
          </button>
          {datePickerOpen && !viewOnly && (
            <DatePicker
              border={border}
              initialDate={date}
              time={false}
              timeZone={tz}
              minDate={minDateObj}
              maxDate={maxDateObj}
              closeDatePicker={() => {
                setDatePickerOpen(false);
              }}
              position={dropdownPosition}
              onSelectDate={(date) => {
                // Create a new UTC date with time zeroed out
                const dateOnly = new Date(
                  Date.UTC(
                    date.getUTCFullYear(),
                    date.getUTCMonth(),
                    date.getUTCDate(),
                    0,
                    0,
                    0,
                    0,
                  ),
                );

                const isoString = dateOnly.toISOString();
                setDate(dateOnly);
                onChangeValue(isoString);
                setDatePickerOpen(false);
              }}
            />
          )}
          {error && (
            <p
              className={`mt-1 body-3-regular ${
                errorVisibility ? "font-error" : "opacity-0"
              }`}
            >
              {error}
            </p>
          )}
        </div>
      ) : (
        <p className="body-2-regular font-secondary" suppressHydrationWarning>
          {formattedDate}
        </p>
      )}
    </>
  );
};
